import React from 'react';

import { TailRecord, AppMode, SEARCH_CARRIERS } from '../types';
import './ControlsBar.css';

interface ControlsBarProps {
  mode: AppMode;
  onModeChange: (mode: AppMode) => void;
  records: TailRecord[];
  selected: TailRecord | null;
  discrepancies: string[];
  checkedDiscreps: Set<number>;
  onToggleDiscrep: (index: number) => void;
  onSelect: (tailNumber: string) => void;
  onRun: () => void;
  onRefresh: () => void;
  loading: boolean;
  loadingTails: boolean;
  canRun: boolean;
  searchCarrier: string;
  onSearchCarrierChange: (carrier: string) => void;
  searchDiscrepancy: string;
  onSearchDiscrepancyChange: (text: string) => void;
}

/** Extract the last 6 characters of a tail number for display. */
function formatShortTail(tailNumber: string): string {
  return tailNumber.length > 6 ? tailNumber.slice(-6) : tailNumber;
}

/** Controls panel with mode toggle, tail selector or search inputs, and actions. */
const ControlsBar: React.FC<ControlsBarProps> = ({
  mode,
  onModeChange,
  records,
  selected,
  discrepancies,
  checkedDiscreps,
  onToggleDiscrep,
  onSelect,
  onRun,
  onRefresh,
  loading,
  loadingTails,
  canRun,
  searchCarrier,
  onSearchCarrierChange,
  searchDiscrepancy,
  onSearchDiscrepancyChange,
}) => (
  <div className="controls-bar">
    <div className="mode-toggle">
      <label className="mode-option">
        <input
          type="radio"
          name="appMode"
          value="inWork"
          checked={mode === 'inWork'}
          onChange={() => onModeChange('inWork')}
          disabled={loading}
        />
        <span className="mode-label">In Work</span>
      </label>
      <label className="mode-option">
        <input
          type="radio"
          name="appMode"
          value="search"
          checked={mode === 'search'}
          onChange={() => onModeChange('search')}
          disabled={loading}
        />
        <span className="mode-label">Search</span>
      </label>
    </div>

    {mode === 'inWork' && (
      <InWorkControls
        records={records}
        selected={selected}
        discrepancies={discrepancies}
        checkedDiscreps={checkedDiscreps}
        onToggleDiscrep={onToggleDiscrep}
        onSelect={onSelect}
        onRun={onRun}
        onRefresh={onRefresh}
        loading={loading}
        loadingTails={loadingTails}
        canRun={canRun}
      />
    )}

    {mode === 'search' && (
      <SearchControls
        searchCarrier={searchCarrier}
        onSearchCarrierChange={onSearchCarrierChange}
        searchDiscrepancy={searchDiscrepancy}
        onSearchDiscrepancyChange={onSearchDiscrepancyChange}
        onRun={onRun}
        loading={loading}
        canRun={canRun}
      />
    )}

    <div className="disclaimer-bar">
      <div className="disclaimer-scroll">
        <span className="disclaimer-text">
          FOR REFERENCE ONLY, NOT TO BE USED IN THE MAINTENANCE OF AIRCRAFT
        </span>
      </div>
    </div>
  </div>
);

/** Props for the IN_WORK mode controls. */
interface InWorkControlsProps {
  records: TailRecord[];
  selected: TailRecord | null;
  discrepancies: string[];
  checkedDiscreps: Set<number>;
  onToggleDiscrep: (index: number) => void;
  onSelect: (tailNumber: string) => void;
  onRun: () => void;
  onRefresh: () => void;
  loading: boolean;
  loadingTails: boolean;
  canRun: boolean;
}

/** IN_WORK mode: tail number dropdown, detail chips, discrepancy checkboxes. */
const InWorkControls: React.FC<InWorkControlsProps> = ({
  records,
  selected,
  discrepancies,
  checkedDiscreps,
  onToggleDiscrep,
  onSelect,
  onRun,
  onRefresh,
  loading,
  loadingTails,
  canRun,
}) => (
  <>
    <div className="controls-top-row">
      <div className="control-group">
        <label htmlFor="tailSelect">Tail Number</label>
        <select
          id="tailSelect"
          aria-label="Select tail number currently in work"
          value={selected?.tailNumber || ''}
          onChange={(e) => onSelect(e.target.value)}
          disabled={loadingTails}
        >
          <option value="">
            {loadingTails ? 'Loading...' : '— Select Tail Number —'}
          </option>
          {records.map((rec) => (
            <option key={rec.tailNumber} value={rec.tailNumber}>
              {formatShortTail(rec.tailNumber)} — {rec.carrier || 'N/A'}
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <div className="detail-chips">
          {selected.carrier && (
            <span className="detail-chip">
              Carrier: <strong>{selected.carrier}</strong>
            </span>
          )}
          {selected.ataCode && (
            <span className="detail-chip">
              ATA: <strong>{selected.ataCode}</strong>
            </span>
          )}
          {selected.station && (
            <span className="detail-chip">
              Location: <strong>{selected.station}</strong>
            </span>
          )}
        </div>
      )}

      <div className="controls-actions">
        <button
          className="btn-refresh"
          onClick={onRefresh}
          disabled={loadingTails}
          aria-label="Refresh tail number list"
        >
          &#x21bb; Refresh
        </button>
        <button
          className="btn-run"
          onClick={onRun}
          disabled={!canRun}
          aria-label="Run maintenance analysis"
        >
          <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          {loading ? 'Analyzing...' : 'Run Analysis'}
        </button>
      </div>
    </div>

    {selected && discrepancies.length > 0 && (
      <div className="discrepancy-section">
        <label className="discrepancy-label">
          {discrepancies.length > 1
            ? 'Select discrepancy(s) to analyze:'
            : 'Discrepancy:'}
        </label>
        <div className="discrepancy-list">
          {discrepancies.map((text, index) => (
            <label key={index} className="discrepancy-item">
              <input
                type="checkbox"
                checked={checkedDiscreps.has(index)}
                onChange={() => onToggleDiscrep(index)}
                disabled={loading}
              />
              <span className="discrepancy-text">{text}</span>
            </label>
          ))}
        </div>
      </div>
    )}
  </>
);

/** Props for the SEARCH mode controls. */
interface SearchControlsProps {
  searchCarrier: string;
  onSearchCarrierChange: (carrier: string) => void;
  searchDiscrepancy: string;
  onSearchDiscrepancyChange: (text: string) => void;
  onRun: () => void;
  loading: boolean;
  canRun: boolean;
}

/** SEARCH mode: carrier dropdown, free text discrepancy input, run button. */
const SearchControls: React.FC<SearchControlsProps> = ({
  searchCarrier,
  onSearchCarrierChange,
  searchDiscrepancy,
  onSearchDiscrepancyChange,
  onRun,
  loading,
  canRun,
}) => (
  <>
    <div className="controls-top-row">
      <div className="control-group">
        <label htmlFor="carrierSelect">Carrier Selection</label>
        <select
          id="carrierSelect"
          aria-label="Select carrier for knowledge base lookup"
          value={searchCarrier}
          onChange={(e) => onSearchCarrierChange(e.target.value)}
          disabled={loading}
        >
          <option value="">— Select Carrier —</option>
          {SEARCH_CARRIERS.map((carrier) => (
            <option key={carrier} value={carrier}>
              {carrier}
            </option>
          ))}
        </select>
      </div>

      <div className="controls-actions">
        <button
          className="btn-run"
          onClick={onRun}
          disabled={!canRun}
          aria-label="Run maintenance analysis"
        >
          <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          {loading ? 'Analyzing...' : 'Run Analysis'}
        </button>
      </div>
    </div>

    {searchCarrier && (
      <div className="discrepancy-section">
        <label className="discrepancy-label" htmlFor="searchDiscrepancy">
          Discrepancy to Analyze
        </label>
        <textarea
          id="searchDiscrepancy"
          className="search-discrepancy-input"
          placeholder="Enter the discrepancy description to analyze..."
          value={searchDiscrepancy}
          onChange={(e) => onSearchDiscrepancyChange(e.target.value)}
          disabled={loading}
          rows={4}
        />
      </div>
    )}
  </>
);

export default ControlsBar;
